import type { BudgetCalculatorInput, MonthlyExpensesCategory } from 'graphql/generated/graphql';
import requireValue from 'utils/requireValue';
import type {
  ILifestyleFormValues,
  TBudgetCalculationValue,
  TFormDataKey,
  TMonthlyExpenseKey,
} from './types';

/** `user_input` when it is a finite number, else `calculated_value`. */
export const userInputOrCalculatedValue = ({
  user_input,
  calculated_value,
}: TBudgetCalculationValue) => {
  if (user_input !== null && Number.isFinite(user_input)) return user_input;
  return calculated_value;
};

export type TMonthlyExpensesCategories = Partial<
  Record<TMonthlyExpenseKey, MonthlyExpensesCategory>
>;

export const toNumberValueKey = <K extends TMonthlyExpenseKey>(key: K): `${K}_number_value` =>
  `${key}_number_value`;

/** The step sent with the values: at least the budget's step, null for a completed calculator. */
export function getSubmitStep(
  currentStep: number | null,
  budgetStep: number | null | undefined,
  calculatorCompleted: boolean | undefined,
) {
  if (calculatorCompleted || currentStep === null) return null;
  // `currentStep < budgetStep` is false for a null or undefined budget step (steps are positive).
  if (typeof budgetStep === 'number' && currentStep < budgetStep) return budgetStep;
  return currentStep;
}

/**
 * The mutation input of the picked values, in the order the picks had. The values the input
 * requires are required by the steps' validation before any submit; a missing one throws here
 * (the API used to reject the request).
 */
export function toBudgetCalculatorInput(
  allowedFormValues: Pick<ILifestyleFormValues, TFormDataKey>,
  monthlyExpensesCategories: TMonthlyExpensesCategories,
): BudgetCalculatorInput {
  const v = allowedFormValues;
  const c = monthlyExpensesCategories;
  return {
    alone_or_partner: requireValue(v.alone_or_partner, 'alone_or_partner'),
    kids_quantity: requireValue(v.kids_quantity, 'kids_quantity'),
    monthly_net_salary: requireValue(v.monthly_net_salary, 'monthly_net_salary'),
    monthly_net_salary_partner: v.monthly_net_salary_partner,
    other_revenues: requireValue(v.other_revenues, 'other_revenues'),
    cars: requireValue(v.cars, 'cars'),
    buying_with_agent: v.buying_with_agent,
    own_funds: v.own_funds,
    amount_of_alimony: requireValue(v.amount_of_alimony, 'amount_of_alimony'),
    monthly_loan: requireValue(v.monthly_loan, 'monthly_loan'),
    project_type: requireValue(v.project_type, 'project_type'),
    square_meters: requireValue(v.square_meters, 'square_meters'),
    broker_commission: v.broker_commission,
    property_transfer_tax: v.property_transfer_tax,
    land_register_fee: v.land_register_fee,
    notary_fee: v.notary_fee,
    age: requireValue(v.age, 'age'),
    socializing: requireValue(c.socializing, 'socializing'),
    luxuries: requireValue(c.luxuries, 'luxuries'),
    new_car: requireValue(c.new_car, 'new_car'),
    taking_a_break: requireValue(c.taking_a_break, 'taking_a_break'),
    hobbies: requireValue(c.hobbies, 'hobbies'),
    technology: requireValue(c.technology, 'technology'),
    pension_and_insurances: requireValue(c.pension_and_insurances, 'pension_and_insurances'),
  };
}
